import { Image } from 'expo-image';
import { Pressable, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { getSlackWorkspaceUrl } from '@/api/partners/integrations/slack/get-slack-workspace-url';
import type { SlackIntegration } from '@/api/partners/integrations/slack/types';
import { GREY_40_10 } from '@/style/colors';
import { Card, Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { Plus } from '@/ui/icons/plus';
import { Loader } from '@/ui/loader';

import { SLACK_ICON_URL } from '../constants';

type Props = {
  integration?: SlackIntegration;
  isLoading?: boolean;
  onConnectPress: () => void;
  onManagePress: () => void;
};

export function SlackIntegrationCard({
  integration,
  isLoading = false,
  onConnectPress,
  onManagePress,
}: Props) {
  const { styles, theme } = useStyles(stylesheet);

  const isConnected = integration?.status !== 'disconnected';
  const workspaceUrl = getSlackWorkspaceUrl(integration?.workspace);

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <Loader style={{ height: 120, width: 120 }} />
      </View>
    );
  }

  return (
    <Card style={styles.card}>
      <View style={styles.header}>
        <View style={styles.titleRow}>
          <Image
            source={{ uri: SLACK_ICON_URL }}
            style={styles.icon}
            contentFit="cover"
          />

          {isConnected && workspaceUrl ? (
            <View style={styles.workspaceText}>
              <Text type="title2">{workspaceUrl}</Text>
            </View>
          ) : null}
        </View>

        <Pressable
          onPress={isConnected ? onManagePress : onConnectPress}
          style={({ pressed }) => [
            styles.connectButton,
            pressed && styles.connectButtonPressed,
          ]}
        >
          {!isConnected ? (
            <Icon
              type="naked"
              icon={<Plus />}
              width={12}
              height={12}
              fillColor={theme.colors.button.secondary.default.text}
            />
          ) : null}
          <Text type="bodyRegular" style={styles.connectButtonText}>
            {isConnected ? 'Manage' : 'Connect'}
          </Text>
        </Pressable>
      </View>

      <Text type="bodyRegular" style={styles.description}>
        Teams receive instant notifications for key updates directly within
        Slack.
      </Text>
    </Card>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  card: {
    width: 334,
    padding: 16,
    gap: 16,
    borderRadius: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  icon: {
    width: 36,
    height: 36,
    borderRadius: 8,
  },
  workspaceText: {
    flex: 1,
    gap: 2,
  },
  connectButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.button.secondary.default.border,
    backgroundColor: GREY_40_10,
  },
  connectButtonPressed: {
    backgroundColor: theme.colors.button.secondary.hover.fill,
  },
  connectButtonText: {
    color: theme.colors.button.secondary.default.text,
  },
  description: {
    color: theme.colors.text.secondary,
  },
  loadingContainer: {
    width: 334,
    height: 128,
    alignItems: 'center',
    justifyContent: 'center',
  },
}));
